/**
 * Imports — Phase 2: upload + preview + commit.
 *
 * Drop a .xlsx/.csv/.tsv → POST /api/imports/preview → render headers,
 * 50-row sample, and a column-mapping UI.
 * Once "name" is mapped and a fileToken is held, the Import button is
 * enabled. Clicking it POSTs /api/imports/commit and shows a result card.
 *
 * Manager+ (the parent /admin route already gates manager+).
 */

import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { importsApi } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { cn } from '../../../lib/cn.js';
import {
  KNOWN_FIELDS,
  type ColumnMapping,
  type ImportPreviewResponse,
  type ImportCommitResponse,
} from '@imza/shared';

export const Route = createFileRoute('/_auth/admin/imports')({
  component: ImportsPage,
});

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT = ['.xlsx', '.csv', '.tsv'];

function ImportsPage() {
  const { toast } = useToast();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [result, setResult] = useState<ImportCommitResponse | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleFile(file: File): Promise<void> {
    if (file.size > MAX_BYTES) {
      toast(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`, 'error');
      return;
    }
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      toast(`Unsupported file type: ${ext}. Use .xlsx, .csv or .tsv.`, 'error');
      return;
    }

    setUploading(true);
    setResult(null);
    try {
      const res = await importsApi.preview(file);
      setPreview(res);
      setMapping(res.mapping);
      setFilename(file.name);
      toast(`Parsed ${res.totalRows} row${res.totalRows === 1 ? '' : 's'}`, 'success');
    } catch (err: any) {
      toast(err?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleCommit(): Promise<void> {
    if (!preview || !mapping.name) return;
    setCommitting(true);
    try {
      const res = await importsApi.commit(preview.fileToken, mapping);
      setResult(res);
      setPreview(null);
      toast(`Done — created ${res.created}, merged ${res.merged}`, 'success');
    } catch (err: any) {
      // Non-fatal: show toast and leave the preview up so user can retry
      toast(err?.message || 'Commit failed', 'error');
    } finally {
      setCommitting(false);
    }
  }

  function reset(): void {
    setPreview(null);
    setFilename(null);
    setMapping({});
    setResult(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function setMappingFor(key: (typeof KNOWN_FIELDS)[number]['key'], header: string): void {
    setMapping((prev) => {
      const next: ColumnMapping = { ...prev };
      if (header === '') delete next[key];
      else next[key] = header;
      return next;
    });
  }

  const mappedHeaders = new Set<string>(
    Object.values(mapping).filter((v): v is string => !!v),
  );

  const canCommit = !!(preview && mapping.name && !committing);

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <h1 className="text-xl font-bold text-primary-text">Imports</h1>
        <p className="text-xs text-secondary mt-0.5">
          Upload an Excel or CSV/TSV. Preview the rows and column mapping, then
          click Import to write to the database.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">

        {/* ── Result card (shown after a successful commit) ── */}
        {result && (
          <div className="bg-white border border-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <h2 className="text-sm font-semibold text-primary-text">
                Import complete — {filename}
              </h2>
            </div>

            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                { label: 'Created', value: result.created, color: 'text-green-700 bg-green-50 border-green-200' },
                { label: 'Merged',  value: result.merged,  color: 'text-blue-700 bg-blue-50 border-blue-200' },
                {
                  label: 'Skipped',
                  value: result.skipped.no_name + result.skipped.junk_name + result.skipped.no_unit + result.skipped.duplicate_unit,
                  color: 'text-amber-700 bg-amber-50 border-amber-200',
                },
                { label: 'Errors',  value: result.errors.length, color: result.errors.length > 0 ? 'text-red-700 bg-red-50 border-red-200' : 'text-secondary bg-[#f7f8fc] border-border' },
              ].map(({ label, value, color }) => (
                <div key={label} className={cn('border rounded-lg p-3', color)}>
                  <div className="text-2xl font-bold">{value.toLocaleString()}</div>
                  <div className="text-xs mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {/* Skipped breakdown */}
            {(result.skipped.no_name > 0 || result.skipped.junk_name > 0 || result.skipped.no_unit > 0 || result.skipped.duplicate_unit > 0) && (
              <div className="text-xs text-secondary space-y-0.5">
                <div className="font-medium text-primary-text mb-1">Skipped breakdown</div>
                {result.skipped.no_name > 0      && <div>No name: {result.skipped.no_name.toLocaleString()} rows</div>}
                {result.skipped.junk_name > 0     && <div>Junk name (placeholder / code): {result.skipped.junk_name.toLocaleString()} rows</div>}
                {result.skipped.no_unit > 0       && <div>Unit number without building (unit skipped): {result.skipped.no_unit.toLocaleString()} rows</div>}
                {result.skipped.duplicate_unit > 0 && <div>Duplicate unit (already exists on landlord): {result.skipped.duplicate_unit.toLocaleString()} rows</div>}
              </div>
            )}

            {/* First 10 errors */}
            {result.errors.length > 0 && (
              <div>
                <div className="text-xs font-medium text-red-700 mb-1">
                  Row errors (showing first {Math.min(10, result.errors.length)} of {result.errors.length})
                </div>
                <div className="rounded-lg border border-red-200 overflow-hidden">
                  <table className="text-xs w-full">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="p-2 text-left font-medium text-red-700 w-20">Row</th>
                        <th className="p-2 text-left font-medium text-red-700">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.slice(0, 10).map(({ rowIndex, message }) => (
                        <tr key={rowIndex} className="border-t border-red-100">
                          <td className="p-2 text-red-900">{rowIndex + 1}</td>
                          <td className="p-2 text-red-800">{message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button variant="secondary" size="sm" onClick={reset}>
              Re-upload
            </Button>
          </div>
        )}

        {/* ── Drop zone (hidden when preview or result is shown) ── */}
        {!preview && !result && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files[0];
              if (f) void handleFile(f);
            }}
            onClick={() => fileInput.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors',
              dragOver ? 'border-primary bg-blue-50' : 'border-border hover:bg-hover-bg',
              uploading && 'opacity-60 pointer-events-none',
            )}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
            }}
            aria-label="Drop file or click to browse"
          >
            <Upload className="w-10 h-10 text-secondary mx-auto mb-2" />
            <p className="text-sm font-medium text-primary-text">
              {uploading ? 'Parsing…' : 'Drop .xlsx, .csv or .tsv here, or click to browse'}
            </p>
            <p className="text-xs text-secondary mt-1">Max 10 MB / 10,000 rows</p>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.csv,.tsv"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </div>
        )}

        {/* ── Preview section ── */}
        {preview && (
          <>
            {/* Summary card */}
            <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-secondary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-primary-text truncate">{filename}</div>
                <div className="text-xs text-secondary mt-0.5">
                  {preview.totalRows.toLocaleString()} row
                  {preview.totalRows === 1 ? '' : 's'} detected · {preview.headers.length} columns
                  · showing first {Math.min(50, preview.totalRows)}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={reset} disabled={committing}>
                Choose another file
              </Button>
            </div>

            {/* Column mapping */}
            <div className="bg-white border border-border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-primary-text mb-3">Column mapping</h2>
              <p className="text-xs text-secondary mb-3">
                Pick which spreadsheet column maps to each system field.
                Auto-detected guesses are pre-selected — adjust if wrong.
                At minimum, <strong>Owner Name</strong> must be mapped.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {KNOWN_FIELDS.map((f) => {
                  const current = mapping[f.key] ?? '';
                  return (
                    <div key={f.key} className="flex flex-col">
                      <label className="text-xs font-medium text-primary-text mb-1">
                        {f.label}
                        {f.key === 'name' && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      <select
                        value={current}
                        onChange={(e) => setMappingFor(f.key, e.target.value)}
                        disabled={committing}
                        className="border border-border rounded-lg px-2 py-1.5 text-sm min-h-[36px] bg-white disabled:opacity-60"
                      >
                        <option value="">— ignore —</option>
                        {preview.headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Unmapped headers notice */}
              {(() => {
                const unmapped = preview.headers.filter((h) => !mappedHeaders.has(h));
                if (unmapped.length === 0) return null;
                return (
                  <div className="mt-3 flex items-start gap-2 text-xs text-secondary bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>
                      Unmapped columns ({unmapped.length}): {unmapped.join(', ')}.
                      They will be ignored on commit.
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Sample rows table */}
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-primary-text">
                  Preview ({preview.sampleRows.length} rows)
                </h2>
                <span className="flex items-center gap-1 text-xs text-secondary">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  Mapped headers highlighted
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead className="bg-[#f7f8fc] sticky top-0">
                    <tr>
                      {preview.headers.map((h) => {
                        const isMapped = mappedHeaders.has(h);
                        return (
                          <th
                            key={h}
                            className={cn(
                              'p-2 text-left border-b border-border whitespace-nowrap font-semibold',
                              isMapped ? 'text-primary' : 'text-secondary',
                            )}
                          >
                            {h}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sampleRows.map((row, ri) => (
                      <tr key={ri} className="border-b border-border last:border-0 hover:bg-hover-bg">
                        {preview.headers.map((h) => (
                          <td
                            key={h}
                            className="p-2 text-primary-text whitespace-nowrap max-w-[240px] truncate"
                            title={row[h] ?? ''}
                          >
                            {row[h] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Commit button */}
            <div className="flex items-center justify-end gap-3">
              {!mapping.name && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  Map "Owner Name" to enable import
                </span>
              )}
              <Button
                variant="primary"
                disabled={!canCommit}
                onClick={() => void handleCommit()}
                aria-disabled={!canCommit}
              >
                {committing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing…
                  </span>
                ) : (
                  `Import ${preview.totalRows.toLocaleString()} rows`
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
